/**
 * `/emergency` — production has stopped, and the clock needs to start.
 *
 * The ticket comes first, always. A phone call with no record is invisible to
 * the SLA matrix: the P1 response target is 30 minutes and the escalation
 * notifications fire at 25/50/75/100% of a clock that only exists once a ticket
 * does. Someone who rings the service desk and gets voicemail has, as far as
 * every report and every escalation is concerned, not reported anything.
 *
 * So this files first and hands over second. What makes that acceptable to
 * somebody standing next to a stopped line is that filing costs **one field and
 * one click** — no category, no service, no impact, no priority. Urgency is
 * HIGH by definition of being here; impact is IT's to set; the subject is taken
 * from the first line of what they typed. Anything else can be added to the
 * ticket afterwards, and the page says so.
 *
 * Then it shows the reference and the Teams link. The reference is the point:
 * it turns "everything's down" into a message about a record the technician
 * can already see on their screen. No phone number (Rusty, 20 Oct): nothing
 * user-facing offers one; people follow the ticket in My tickets.
 *
 * Discoverability is deliberately asymmetric — a quiet link in the footer,
 * present on every page, absent from the conversation flow. An emergency button
 * in the header gets used for password resets within a month, and a channel
 * that cries wolf is worse than no channel.
 */

import {
  ChevronRight,
  CircleCheck,
  FilePlus,
  Icon,
  KeyRound,
  Mail,
  MessageSquare,
  Monitor,
  ShieldAlert,
  TriangleAlert,
} from "@norican/icons";
import {
  Alert,
  Button,
  Card,
  cn,
  Field,
  Input,
  Radio,
  RadioGroup,
  Textarea,
  Typography,
} from "@norican/react";
import { useEffect, useId, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";

import { AttachmentUploader, usePasteToAttach } from "@/components/attachments";
import { AppBreadcrumbs, ErrorState, PageContainer } from "@/components/common";
import { FilingAsLine } from "@/components/layout";
import {
  KioskTicketNumberNote,
  LookupSelect,
  type TicketCreateRequest,
  type TicketDetail,
  ticketReferenceKey,
  useCreateTicket,
  useTicketMetadata,
} from "@/features/tickets";
import { newIdempotencyKey } from "@/features/tickets/api/operations";
import { useDocumentTitle } from "@/hooks";
import { useLanguage, useT } from "@/i18n";
import { useKiosk, useMe } from "@/lib/api";
import { logger } from "@/lib/logger";

import { EMERGENCY_TEAMS_URL } from "./emergency-contact";
import { FiledPriority } from "./filed-priority";
import {
  emergencyCodes,
  emergencyPriority,
  SECURITY_CHOICES,
  type SecurityChoice,
  securityChoiceTarget,
  showEmergencyDetails,
} from "./lib/priority";

/** The four situations this form is for. Also recorded on the ticket.
 *
 * SECURITY is last (20 Oct) so its "What happened?" panel opens straight
 * under it, outside the radio group. */
const CRITICAL_KINDS = ["PRODUCTION", "UNAVAILABLE", "OTHER", "SECURITY"] as const;
type CriticalKind = (typeof CRITICAL_KINDS)[number];

/** One picture per "What happened?" choice, so each reads at a glance. */
const SECURITY_CHOICE_ICONS = { EMAIL: Mail, CLICKED: KeyRound, OTHER: Monitor } as const;

/** How wide the blast radius is, in the reporter's own terms.
 *
 * Recorded on the ticket for whoever picks it up. Since 19 Oct it also sets
 * the priority for one case only: a cybersecurity issue that affects one
 * person is a Priority 2 (`lib/priority.ts`). */
const IMPACT_CHOICES = ["ONE", "SEVERAL", "SITE", "MULTI"] as const;
type ImpactChoice = (typeof IMPACT_CHOICES)[number];

/** Give up waiting for the filing after this long and show the error. */
const CREATE_TIMEOUT_MS = 30_000;
/** Offer the Teams chat while still waiting, after this long. */
const SLOW_NOTICE_MS = 10_000;

/** Ivanti caps the subject at 255; a first line longer than this is a paragraph. */
const SUBJECT_MAX = 120;

/** The first sentence or line of what they typed, which is what a subject is. */
function deriveSubject(text: string): string {
  const firstLine = text.trim().split(/\r?\n/)[0] ?? text.trim();
  const trimmed = firstLine.trim();
  return trimmed.length > SUBJECT_MAX ? `${trimmed.slice(0, SUBJECT_MAX - 1)}…` : trimmed;
}

export function EmergencyPage() {
  const { t } = useT("tickets");
  const { language } = useLanguage();
  const navigate = useNavigate();
  const create = useCreateTicket();
  const { data: me } = useMe();
  const kiosk = useKiosk();
  /* The tenant's site list. `Incident.CustomerLocation` only accepts a name
     from it — free text is refused outright — so a picker is the only way the
     site reaches the record rather than the prose. */
  const { data: metadata } = useTicketMetadata();
  const sites = metadata?.locations ?? [];

  const [kind, setKind] = useState<CriticalKind | null>(null);
  /* 20 Oct: with SECURITY picked, "What happened?" — a suspicious email goes
     to its own page; anything else carries on with this form. */
  const [securityChoice, setSecurityChoice] = useState<SecurityChoice | null>(null);
  const showDetails = showEmergencyDetails(kind, securityChoice);
  const [text, setText] = useState("");
  const [site, setSite] = useState("");
  const [system, setSystem] = useState("");
  const [impact, setImpact] = useState<ImpactChoice>("SITE");
  const [started, setStarted] = useState("");
  const [contact, setContact] = useState("");
  const [attachmentIds, setAttachmentIds] = useState<string[]>([]);
  /* This page has no `<form>` element, so the card's field wrapper is the
     enclosing element the paste handler goes on. Same reach either way — every
     field of the report is inside it. */
  const paste = usePasteToAttach({ disabled: create.isPending });
  const [uploading, setUploading] = useState(false);
  const [filed, setFiled] = useState<TicketDetail | null>(null);
  /* One Idempotency-Key per attempt, as `useTicketSubmit` does it: the same
     while the body is unchanged, so pressing "file" again after a timeout
     replays the first P1 rather than raising a second one; editing anything
     makes a new body and so a new key. */
  const attempt = useRef<{ fingerprint: string; key: string } | null>(null);
  /* Everything is read-only while the P1 is being filed. An edit mid-flight
     would change the fingerprint, so a retry after a timeout would carry a new
     Idempotency-Key and raise a second P1 instead of replaying the first. */
  const locked = create.isPending;
  /* Someone at a stopped line must not watch a spinner for long: after a few
     seconds the Teams chat is offered beside it. */
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (!create.isPending) {
      setSlow(false);
      return;
    }
    const timer = window.setTimeout(() => setSlow(true), SLOW_NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [create.isPending]);

  /* What this report will be filed as, so the page never promises a P1 that
     the server will not file (19 Oct). */
  const priority = emergencyPriority(kind, impact);
  const tooShort = text.trim().length < 3;

  /* The panel takes focus when it opens, so a keyboard or screen reader user
     hears the question; "something else" hands focus to the description. */
  const securityPanelRef = useRef<HTMLElement>(null);
  const securityHeadingId = useId();
  const whatRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (kind === "SECURITY") {
      securityPanelRef.current?.focus();
    }
  }, [kind]);
  const chooseSomethingElse = () => {
    setSecurityChoice("OTHER");
    /* The fields are already mounted (only hidden), so they can take focus
       once React has shown them. */
    window.requestAnimationFrame(() => whatRef.current?.focus());
  };
  const incomplete = tooShort || kind === null;
  /* The button is disabled until both required answers are there, and a
     disabled button says nothing about why — so the line under it does. */
  const missingHint =
    kind === null && tooShort
      ? t("emergency.missingBoth")
      : kind === null
        ? t("emergency.missingKind")
        : tooShort
          ? t("emergency.missingText")
          : null;
  const missingHintId = useId();

  /* Once filed, focus moves to the confirmation so a screen reader (and a
     phone scrolled down to the button) lands on the reference number. */
  const filedHeadingRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (filed !== null) {
      filedHeadingRef.current?.focus();
    }
  }, [filed]);

  const handleSubmit = () => {
    if (incomplete || !showDetails || uploading || create.isPending) {
      return;
    }
    const what = text.trim();
    logger.debug("[emergency] filing critical incident", {
      kind,
      impact,
      attachments: attachmentIds.length,
    });

    /* The structured answers go into the description as labelled lines rather
       than being dropped. Ivanti gives us `location` and `phone` as real
       fields and nothing for "which system" or "since when", and a technician
       reading the ticket needs all four — so the ones without a home are
       written into the body under their own headings, which is what the
       service desk would otherwise have to ask for. */
    /* The site only goes in the prose when it could not go in the field: with
       no picker there is nowhere else to put it, and a technician reading it
       off the body is how this worked before the list existed. */
    const siteInDescription = sites.length === 0 ? site.trim() : "";
    const details = [
      [t("emergency.kindLabel"), t(`emergency.kind.${kind}`)],
      [t("emergency.siteLabel"), siteInDescription],
      [t("emergency.systemLabel"), system.trim()],
      [t("emergency.impactLabel"), t(`emergency.impact.${impact}`)],
      [t("emergency.startedLabel"), started.trim()],
      [t("emergency.contactLabel"), contact.trim()],
    ]
      .filter(([, value]) => value !== "")
      .map(([label, value]) => `${label}: ${value}`)
      .join("\n");

    const body: TicketCreateRequest = {
      kind: "INCIDENT",
      subject: deriveSubject(what),
      description: `${what}\n\n${details}`,
      body_lang: language,
      /* HIGH and HIGH, a Priority 1, for every critical situation, whatever
         the reporter said about spread — with one exception (19 Oct,
         incident 940: a single suspicious email became a P1). A
         cybersecurity issue that affects one person is a Priority 2
         (Medium / High). A suspicious email has its own page, reached from
         the SECURITY option's "What happened?" panel. The server applies the same rule from
         `emergency`; the codes are sent too for an older server. */
      ...emergencyCodes(priority),
      emergency: {
        kind: kind ?? "OTHER",
        affected: impact,
        site: siteInDescription === "" ? null : siteInDescription,
        system: system.trim() === "" ? null : system.trim(),
        started: started.trim() === "" ? null : started.trim(),
      },
      /* Files on a sentence: no follow-up questions and no report
         sections. When a line is stopped the fastest complete-enough
         ticket beats the most complete slow one, and IT gets the rest by
         phone within minutes. */
      critical: true,
      category_id: null,
      service_id: null,
      // `Incident.CustomerLocation` is validated against the tenant's
      // `Locations` list and refuses anything else, so this is either a
      // name straight off that list or nothing at all. When the list could
      // not be read the site falls back into the description above.
      location: sites.length === 0 || site === "" ? null : site,
      phone: contact.trim() === "" ? null : contact.trim(),
      // A photo of the stopped HMI or the error dialog. The server files
      // these onto the Ivanti incident itself, so the technician opens the
      // record and sees what the reporter was looking at.
      attachment_ids: attachmentIds,
    };

    const fingerprint = JSON.stringify(body);
    if (attempt.current === null || attempt.current.fingerprint !== fingerprint) {
      attempt.current = { fingerprint, key: newIdempotencyKey() };
    }

    create.mutate(
      {
        body,
        idempotencyKey: attempt.current.key,
        // Fails into the error state below instead of spinning forever; the
        // same key on retry replays the P1 if it did land.
        signal: AbortSignal.timeout(CREATE_TIMEOUT_MS),
      },
      {
        onSuccess: (ticket) => {
          logger.debug("[emergency] filed", ticket.ticket_number);
          // A fresh key for whatever is reported next.
          attempt.current = null;
          setFiled(ticket);
        },
        onError: (error) => logger.warn("[emergency] filing failed", error),
      },
    );
  };

  useDocumentTitle(t("emergency.title"));

  return (
    <PageContainer
      title={
        <Typography as="span" variant="display-md" className="text-3xl sm:text-4xl">
          {t("emergency.title")}
        </Typography>
      }
      /* Says what the form is for, not a priority: that depends on the
         answers (a one-person security issue is a P2) and is stated by the
         button, then by the confirmation from what the server filed (20 Oct). */
      subtitle={filed === null ? t("emergency.subtitle") : undefined}
      breadcrumbs={<AppBreadcrumbs items={[{ label: t("emergency.title") }]} />}
      actions={
        me === undefined ? null : (
          <Typography as="span" variant="caption" tone="muted">
            {t("emergency.reportingAs", {
              name: [me.first_name, me.last_name].filter(Boolean).join(" ") || me.email,
            })}
          </Typography>
        )
      }
    >
      <div className="flex max-w-[var(--norican-size-container-md)] flex-col gap-6">
        {filed === null ? (
          <>
            {/* Before anything else. Someone reaching for an emergency form
                with a colleague hurt must be turned around immediately, and
                that cannot sit below six fields. */}
            <Alert tone="warning" title={t("emergency.safety")} />

            <Card padding="lg">
              <div className="flex flex-col gap-5" onPaste={paste.onPaste}>
                <Field
                  name="critical_kind"
                  label={t("emergency.kindLabel")}
                  required
                  disabled={locked}
                >
                  <RadioGroup
                    value={kind ?? ""}
                    onValueChange={(value) => {
                      setKind(value as CriticalKind);
                      if (value !== "SECURITY") {
                        setSecurityChoice(null);
                      }
                    }}
                    disabled={locked}
                    className="flex flex-col gap-2"
                  >
                    {CRITICAL_KINDS.map((option) => (
                      <Radio.Root
                        key={option}
                        value={option}
                        label={t(`emergency.kind.${option}`)}
                      />
                    ))}
                  </RadioGroup>
                </Field>

                {kind === "SECURITY" ? (
                  <section
                    ref={securityPanelRef}
                    tabIndex={-1}
                    aria-labelledby={securityHeadingId}
                    className="border-accent-border bg-accent-subtle/40 focus-visible:ring-accent -mt-2 flex flex-col gap-3 rounded-lg border-2 p-4 focus:outline-none focus-visible:ring-2 sm:p-5"
                  >
                    <div className="flex items-center gap-2">
                      <Icon
                        icon={ShieldAlert}
                        size="md"
                        className="text-accent-fg-subtle shrink-0"
                      />
                      <Typography as="h2" variant="heading-5" id={securityHeadingId}>
                        {t("emergency.security.title")}
                      </Typography>
                    </div>
                    <ul className="flex flex-col gap-2">
                      {SECURITY_CHOICES.map((choice) => {
                        const target = securityChoiceTarget(choice);
                        const content = (
                          <>
                            <Icon
                              icon={SECURITY_CHOICE_ICONS[choice]}
                              size="md"
                              className="shrink-0"
                            />
                            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                              <span className="font-semibold text-pretty">
                                {t(`emergency.security.choice.${choice}`)}
                              </span>
                              <span
                                className={
                                  choice === "EMAIL"
                                    ? "text-sm text-pretty opacity-90"
                                    : "text-fg-muted text-sm text-pretty"
                                }
                              >
                                {t(`emergency.security.choiceHint.${choice}`)}
                              </span>
                            </span>
                            {target !== null ? (
                              <Icon icon={ChevronRight} size="sm" className="shrink-0" />
                            ) : securityChoice === "OTHER" ? (
                              <Icon
                                icon={CircleCheck}
                                size="sm"
                                className="text-accent-fg-subtle shrink-0"
                              />
                            ) : null}
                          </>
                        );
                        const choiceClass = cn(
                          "flex min-h-14 w-full items-center gap-3 rounded-md border-2 px-4 py-3 text-left transition-colors focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:outline-none",
                          choice === "EMAIL"
                            ? "bg-accent text-fg-on-accent border-accent hover:bg-accent-hover"
                            : "bg-surface border-border hover:border-accent-border hover:bg-surface-hover",
                          choice === "CLICKED" && "[&>svg:first-child]:text-danger-fg",
                          choice === "OTHER" &&
                            securityChoice === "OTHER" &&
                            "border-accent bg-accent-subtle",
                          locked && "pointer-events-none opacity-60",
                        );
                        return (
                          <li key={choice}>
                            {target === null ? (
                              <button
                                type="button"
                                aria-pressed={securityChoice === "OTHER"}
                                disabled={locked}
                                onClick={chooseSomethingElse}
                                className={choiceClass}
                              >
                                {content}
                              </button>
                            ) : (
                              <Link
                                to={target}
                                aria-disabled={locked || undefined}
                                tabIndex={locked ? -1 : undefined}
                                className={choiceClass}
                              >
                                {content}
                              </Link>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                ) : null}

                {/* The way out for everyone who does not belong here — placed
                    with the question rather than at the foot of the page. */}
                <Typography as="p" variant="caption" tone="muted" className="text-pretty">
                  {t("emergency.kindFooter")}{" "}
                  <Link
                    to="/ask"
                    className="text-accent-fg-subtle font-medium underline-offset-2 hover:underline"
                  >
                    {t("emergency.backToHelpDesk")}
                  </Link>
                </Typography>

                {/* Hidden, not unmounted, while the SECURITY panel waits for a
                    choice: the description keeps its text and its autoFocus
                    does not fire again on every change of situation. */}
                <div className={showDetails ? "flex flex-col gap-5" : "hidden"}>
                  <Field
                    name="what_is_happening"
                    label={t("emergency.whatLabel")}
                    hint={t("emergency.whatHint")}
                    required
                    disabled={locked}
                  >
                    <Textarea
                      ref={whatRef}
                      rows={3}
                      autoFocus
                      value={text}
                      onChange={(event) => setText(event.target.value)}
                      disabled={locked}
                      placeholder={t("emergency.whatPlaceholder")}
                    />
                  </Field>

                  {/* Directly under the description, because a photo of the
                      stopped screen is usually a better report than the sentence
                      someone can type while standing next to it — and because
                      asking for it after six more fields means never getting it.
                      Optional, and the hint says so: this form's promise is one
                      field and one click, and a P1 must never wait on a picture. */}
                  <Field
                    name="evidence"
                    label={t("emergency.attachLabel")}
                    hint={t("emergency.attachHint")}
                    disabled={locked}
                  >
                    <AttachmentUploader
                      ref={paste.ref}
                      id="evidence"
                      onChange={setAttachmentIds}
                      onPendingChange={setUploading}
                      disabled={create.isPending}
                    />
                  </Field>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field name="site" label={t("emergency.siteLabel")} disabled={locked}>
                      {/* Falls back to free text when the tenant list is
                          unreadable. It will not reach `CustomerLocation` that
                          way, but it still reaches the technician through the
                          description — and a P1 must never be blocked on a
                          dropdown that failed to load. */}
                      {sites.length === 0 ? (
                        <Input
                          value={site}
                          onChange={(event) => setSite(event.target.value)}
                          disabled={locked}
                          placeholder={t("emergency.sitePlaceholder")}
                        />
                      ) : (
                        <LookupSelect
                          items={sites}
                          value={site === "" ? null : site}
                          onValueChange={(next) => setSite(next ?? "")}
                          disabled={locked}
                          placeholder={t("emergency.siteSelect")}
                          aria-label={t("emergency.siteLabel")}
                        />
                      )}
                    </Field>

                    <Field name="system" label={t("emergency.systemLabel")} disabled={locked}>
                      <Input
                        value={system}
                        onChange={(event) => setSystem(event.target.value)}
                        disabled={locked}
                        placeholder={t("emergency.systemPlaceholder")}
                      />
                    </Field>
                  </div>

                  <Field name="impact" label={t("emergency.impactLabel")} disabled={locked}>
                    <RadioGroup
                      value={impact}
                      onValueChange={(value) => setImpact(value as ImpactChoice)}
                      disabled={locked}
                      className="flex flex-wrap gap-x-5 gap-y-2"
                    >
                      {IMPACT_CHOICES.map((option) => (
                        <Radio.Root
                          key={option}
                          value={option}
                          label={t(`emergency.impact.${option}`)}
                        />
                      ))}
                    </RadioGroup>
                  </Field>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field name="started" label={t("emergency.startedLabel")} disabled={locked}>
                      <Input
                        value={started}
                        onChange={(event) => setStarted(event.target.value)}
                        disabled={locked}
                        placeholder={t("emergency.startedPlaceholder")}
                      />
                    </Field>

                    <Field name="contact" label={t("emergency.contactLabel")} disabled={locked}>
                      <Input
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel"
                        value={contact}
                        onChange={(event) => setContact(event.target.value)}
                        disabled={locked}
                        placeholder={t("emergency.contactPlaceholder")}
                      />
                    </Field>
                  </div>

                  <Typography as="p" variant="caption" tone="muted">
                    {t("emergency.scope")}
                  </Typography>

                  {create.isError ? (
                    <ErrorState variant="inline" error={create.error} offerHelpdeskContact />
                  ) : null}

                  {slow && create.isPending ? (
                    <Alert tone="info" title={t("emergency.slow.title")}>
                      <div className="flex flex-col items-start gap-2">
                        <Typography as="p" variant="body-sm" className="text-pretty">
                          {t("emergency.slow.body")}
                        </Typography>
                        <Button
                          variant="outline"
                          tone="neutral"
                          size="sm"
                          startIcon={<Icon icon={MessageSquare} size="sm" />}
                          onClick={() => window.open(EMERGENCY_TEAMS_URL, "_blank", "noreferrer")}
                        >
                          {t("emergency.teams")}
                        </Button>
                      </div>
                    </Alert>
                  ) : null}

                  <div className="flex flex-col gap-2">
                    {/* Shared PCs: whose name this P1 goes out under. */}
                    <FilingAsLine />
                    <Button
                      type="button"
                      tone="danger"
                      size="lg"
                      loading={create.isPending || uploading}
                      disabled={incomplete || uploading}
                      aria-describedby={missingHint === null ? undefined : missingHintId}
                      startIcon={<Icon icon={TriangleAlert} size="sm" />}
                      onClick={handleSubmit}
                      className="self-start"
                    >
                      {uploading ? t("emergency.submitUploading") : t("emergency.submit")}
                    </Button>
                    {missingHint === null ? null : (
                      <Typography as="p" id={missingHintId} variant="caption" tone="muted">
                        {missingHint}
                      </Typography>
                    )}
                    <Typography as="p" variant="body-sm" tone="muted">
                      {priority === "P2" ? t("emergency.submitNoteP2") : t("emergency.submitNote")}
                    </Typography>
                  </div>
                </div>
              </div>
            </Card>
          </>
        ) : (
          <>
            {/* The reference is the single most useful object on this screen:
                it is what the employee quotes to IT. So it is set in
                the display face at heading size, monospaced so digits cannot be
                misread, and it links to the ticket. Everything else on the page
                is arranged around it. */}
            <Card padding="lg" className="border-success-border bg-success-subtle/40">
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <Icon icon={CircleCheck} size="md" className="text-success-fg shrink-0" />
                  <Typography
                    as="h2"
                    variant="heading-4"
                    ref={filedHeadingRef}
                    tabIndex={-1}
                    className="focus:outline-none"
                  >
                    {t("emergency.filedTitle")}
                  </Typography>
                </div>

                <div className="flex flex-col gap-1">
                  <Typography as="span" variant="caption" tone="muted">
                    {t("emergency.referenceLabel")}
                  </Typography>
                  <Link
                    to={filed.url_path}
                    className="text-accent-fg-subtle font-mono text-3xl font-semibold tracking-tight underline-offset-4 hover:underline sm:text-4xl"
                  >
                    {t(ticketReferenceKey(filed.kind, "id"), { number: filed.ticket_number })}
                  </Link>
                </div>

                <FiledPriority code={filed.priority_code} />

                <Typography as="p" variant="body-sm" tone="muted" className="text-pretty">
                  {t("emergency.quoteIt")}
                </Typography>
              </div>
            </Card>

            <KioskTicketNumberNote number={filed.ticket_number} />

            {/* Now the fast channel, and where to follow it. No phone number
                (20 Oct): the HelpDesk keeps the person on the ticket. A shared
                device has no My tickets of its own, so the line is left out. */}
            <div className="flex flex-col gap-2">
              <Button
                variant="outline"
                tone="neutral"
                startIcon={<Icon icon={MessageSquare} size="sm" />}
                onClick={() => window.open(EMERGENCY_TEAMS_URL, "_blank", "noreferrer")}
                className="self-start"
              >
                {t("emergency.teams")}
              </Button>
              {kiosk === null ? (
                <Link
                  to="/tickets"
                  className="text-accent-fg-subtle text-sm font-medium underline-offset-2 hover:underline"
                >
                  {t("emergency.followUpdates")}
                </Link>
              ) : null}
            </div>

            {/* Detail is worth real estate: everything added here is a question
                the technician does not have to ask, and this is the moment the
                employee still has it all in their head. */}
            <Card padding="md">
              <div className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  tone="accent"
                  startIcon={<Icon icon={FilePlus} size="sm" />}
                  onClick={() => void navigate(filed.url_path)}
                  className="self-start"
                >
                  {t("emergency.addDetail")}
                </Button>
                <Typography as="p" variant="caption" tone="muted" className="text-pretty">
                  {t("emergency.addDetailHint")}
                </Typography>
              </div>
            </Card>

            <Link
              to="/ask"
              className="text-accent-fg-subtle text-sm font-medium underline-offset-2 hover:underline"
            >
              {t("emergency.backToHelpDesk")}
            </Link>
          </>
        )}
      </div>
    </PageContainer>
  );
}
